/**
 * Custom Collision Detection for Kanban Drag & Drop
 * 
 * Handles both column and card dragging with different strategies
 */

import {
  closestCenter,
  closestCorners,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';

/**
 * Custom collision detection that handles both column and card dragging
 * - When dragging a column: only consider other columns as drop targets
 * - When dragging a card: consider cards and column droppables
 * - When dragging a card in swimlane mode: only consider drop targets in the same swimlane
 * 
 * @param {string} activeType - 'column' or 'card'
 * @param {string[]} columnIds - Array of column IDs
 * @param {string|null} activeCardSwimlaneId - Swimlane ID of the dragged card (null if not in swimlane mode)
 */
export const createCollisionDetection = (activeType, columnIds, activeCardSwimlaneId = null) => (args) => {
  const { droppableContainers } = args;
  
  // When dragging a column, only consider other columns
  if (activeType === 'column') {
    // Filter to only include column sortables (not card sortables or droppables)
    const columnContainers = droppableContainers.filter(container => {
      const id = container.id;
      // Include only column IDs (not prefixed ones like 'column-drop-xxx')
      return columnIds.includes(id);
    });
    
    // Use closestCenter with only column containers
    const filteredArgs = {
      ...args,
      droppableContainers: columnContainers,
    };
    
    return closestCenter(filteredArgs);
  }
  
  // For cards in swimlane mode, filter to only allow drops in the same swimlane
  let filteredContainers = droppableContainers;
  
  if (activeCardSwimlaneId) {
    filteredContainers = droppableContainers.filter(container => {
      const id = String(container.id);
      
      // Check if this is a swimlane droppable: swimlane-drop-{swimlaneId}-{columnId}
      if (id.startsWith('swimlane-drop-')) {
        const withoutPrefix = id.replace('swimlane-drop-', '');
        // The swimlane ID is the first part before the column ID
        // Handle both simple IDs and IDs that might contain dashes
        // Format: {swimlaneId}-{columnId}
        // We need to find where the swimlane ID ends
        
        // Check if it starts with our swimlane ID
        const swimlaneIdStr = String(activeCardSwimlaneId);
        if (withoutPrefix.startsWith(swimlaneIdStr + '-')) {
          return true; // Same swimlane
        }
        return false; // Different swimlane
      }
      
      // Check if this is a card within a swimlane
      // Cards have data.type === 'card' and we need to check their parent swimlane
      const containerData = container.data?.current;
      if (containerData?.type === 'card') {
        // The card's swimlane is determined by its parent droppable
        // We can check if the card belongs to the same swimlane by checking the DOM
        // or by the swimlaneId stored in the data
        if (containerData.swimlaneId !== undefined) {
          return containerData.swimlaneId === activeCardSwimlaneId;
        }
        // If no swimlaneId in data, allow it (fallback for non-swimlane mode)
        return true;
      }
      
      // Allow other containers (like column droppables in non-swimlane mode)
      return true;
    });
  }
  
  const filteredArgs = {
    ...args,
    droppableContainers: filteredContainers,
  };
  
  // For cards, use the original logic with filtered containers
  // First check for pointer intersections (most accurate)
  const pointerCollisions = pointerWithin(filteredArgs);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  
  // Fall back to rect intersection
  const rectCollisions = rectIntersection(filteredArgs);
  if (rectCollisions.length > 0) {
    return rectCollisions;
  }
  
  // Finally try closest corners
  return closestCorners(filteredArgs);
};

